import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/kfmApi";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, BookMarked } from "lucide-react";
import { toast } from "sonner";

function fmt(d) {
  if (!d) return null;
  try { return new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return null; }
}

// Editable house rules for tone/copy/image/video style, injected into Shee's system prompt
// on every conversation. Shee can also append to this herself via update_brand_guidelines.
export default function BrandGuidelines() {
  const [content, setContent] = useState(null);
  const [meta, setMeta] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/brand-guidelines");
      setContent(data.content || "");
      setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load brand guidelines");
      setContent("");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/brand-guidelines", { content });
      setMeta({ updated_at: data.updated_at, updated_by: "you" });
      toast.success("Brand guidelines saved — Shee will follow these from now on.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save");
    }
    setSaving(false);
  };

  if (content === null) {
    return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-[#00E5FF] animate-spin" strokeWidth={1.5} /></div>;
  }

  return (
    <div data-testid="admin-brand-guidelines" className="space-y-5">
      <header className="space-y-1">
        <h2 className="text-2xl text-white flex items-center gap-2">
          <BookMarked className="w-5 h-5 text-[#00E5FF]" strokeWidth={1.75} /> Brand Guidelines
        </h2>
        <p className="text-sm text-slate-400 leading-relaxed">
          The house rules for tone, copy, photography and video style. This doc is injected into Shee&rsquo;s
          system prompt on every conversation, so she treats it as binding — she can also append to it herself
          when you ask her to remember something as a permanent rule.
        </p>
        {meta.updated_at && (
          <p className="text-xs text-slate-500" data-testid="brand-guidelines-updated-meta">
            Last updated {fmt(meta.updated_at)}{meta.updated_by ? ` by ${meta.updated_by}` : ""}
          </p>
        )}
      </header>

      <Textarea
        data-testid="brand-guidelines-textarea"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={24}
        className="font-mono text-sm bg-[#0E131F] border-[#1E273D] text-slate-200 leading-relaxed"
      />

      <div className="flex items-center gap-3">
        <Button
          data-testid="brand-guidelines-save-button"
          onClick={save}
          disabled={saving || !content.trim()}
          className="bg-[#00E5FF] text-[#07090E] hover:bg-[#33ECFF]"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.75} /> : null}
          Save guidelines
        </Button>
        <Button
          data-testid="brand-guidelines-reload-button"
          variant="outline"
          onClick={load}
          disabled={saving}
          className="border-[#29344D] text-slate-300 hover:border-[#00E5FF]/50"
        >
          Discard changes / reload
        </Button>
      </div>
    </div>
  );
}
